import { useEffect, useRef, useState } from "react";
import { MapPin, Crosshair } from "lucide-react";
import { api } from "@/lib/api";

const GPS_HINT = /-?\d{1,3}\.\d{3,}|\d{1,3}\s*[°º]/;

export function LocationField({ value, onChange, incident, disabled, onOpenMap, onSetPoint }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [gpsAmbiguous, setGpsAmbiguous] = useState(null);
  const box = useRef(null);

  useEffect(() => {
    if (!value || value.length < 2 || disabled) { setSuggestions([]); return undefined; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/geo/locations/suggest", { params: { q: value } }); setSuggestions(data); } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [value, disabled]);

  useEffect(() => {
    if (!value || !GPS_HINT.test(value) || incident?.location_source) { setGpsAmbiguous(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/geo/parse-gps", { text: value });
        const m = data.matches?.[0];
        setGpsAmbiguous(m && m.ambiguous ? m : null);
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [value, incident?.location_source]);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const badge = incident?.location_validated
    ? { label: "VALIDATED", cls: "border-teal-500/60 text-teal-700 dark:text-teal-300 bg-teal-500/10" }
    : { label: "UNVALIDATED", cls: "border-slate-500/50 text-slate-600 dark:text-slate-300 bg-slate-500/10" };
  const src = incident?.location_source;
  const srcLabel = { pin: "PIN-DROP", gps_text: "GPS", gps_narrative: "GPS (NARRATIVE)", gps_manual: "GPS (TAGGED)", geocoded: "GEOCODED", address_record: "ADDRESS RECORD" }[src];

  return (
    <div className="relative" ref={box}>
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <input
            data-testid="location-input"
            className="field-input pr-8"
            value={value}
            disabled={disabled}
            placeholder="Address, intersection, landmark, highway, GPS coordinates…"
            onChange={(e) => { onChange(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Tab") setOpen(false); }}
            autoComplete="off"
          />
          <MapPin className="h-3.5 w-3.5 text-muted-foreground absolute right-2.5 top-2.5" />
        </div>
        <button data-testid="location-map-button" type="button" onClick={onOpenMap} title="Open map / drop a pin" className="h-8 px-2 rounded-md border text-[11px] mono hover:bg-accent flex items-center gap-1">
          <Crosshair className="h-3.5 w-3.5" /> MAP
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-1">
        <span data-testid="location-validation-badge" className={`chip mono ${badge.cls}`}>{badge.label}</span>
        {srcLabel && <span data-testid="location-source-badge" className="chip mono border-blue-500/50 text-blue-700 dark:text-blue-300 bg-blue-500/10">{srcLabel}{incident?.location_lat != null ? ` · ${incident.location_lat.toFixed(4)}, ${incident.location_lng.toFixed(4)}` : ""}</span>}
        {incident?.district_name && <span data-testid="location-district-badge" className="chip mono border-primary/50 text-primary bg-primary/10">{incident.district_name.replace(" (sample)", "")}</span>}
        {incident && !incident.location_pending && incident.location_lat == null && <span className="chip mono border-amber-500/50 text-amber-700 dark:text-amber-300 bg-amber-500/10" data-testid="location-no-coords-badge">NO COORDINATES — drop a pin if known</span>}
        {incident?.location_pending && incident.status !== "draft" && <span className="chip mono border-amber-500/60 text-amber-700 dark:text-amber-300 bg-amber-500/10 pulse-soft" data-testid="location-pending-badge">LOCATION PENDING</span>}
        {incident?.location_canonical && <span className="mono text-[10px] text-muted-foreground" data-testid="location-canonical">canonical: {incident.location_canonical}</span>}
      </div>
      {gpsAmbiguous && !disabled && (
        <div className="mt-1 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1.5 text-xs flex flex-wrap items-center gap-2" data-testid="gps-ambiguous-prompt">
          <span>Coordinate order unclear — which is correct?</span>
          <button data-testid="gps-ambiguous-option-0" onClick={() => onSetPoint(gpsAmbiguous.lat, gpsAmbiguous.lng, "gps_text")} className="h-6 px-2 rounded border mono hover:bg-accent">lat {gpsAmbiguous.lat}, lng {gpsAmbiguous.lng}</button>
          {gpsAmbiguous.alternatives.map((a, i) => (
            <button key={i} data-testid={`gps-ambiguous-option-${i + 1}`} onClick={() => onSetPoint(a.lat, a.lng, "gps_text")} className="h-6 px-2 rounded border mono hover:bg-accent">lat {a.lat}, lng {a.lng}</button>
          ))}
        </div>
      )}
      {open && suggestions.length > 0 && !disabled && (
        <div className="absolute z-30 mt-1 w-full rounded-md border bg-popover shadow-lg overflow-hidden" data-testid="location-suggestions">
          {suggestions.map((s) => (
            <button key={s.id} data-testid={`location-suggestion-${s.id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(s.raw); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent flex items-center gap-2">
              <MapPin className="h-3 w-3 text-teal-500" />
              <span className="font-medium">{s.raw}</span>
              {s.common_name && <span className="text-muted-foreground">· {s.common_name}</span>}
              <span className="ml-auto mono text-[10px] text-muted-foreground">{s.canonical}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
